import {
  AsyncLoadingSection,
  FormColumn,
  PageCard,
  PageCardRow,
  PageCardSection,
  TaskPageHeaderLayout,
} from 'Components/Display';
import { SmartInputBlock } from 'Components/Form';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import { Collapse } from 'react-collapse';
import { Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { useFormStateValue } from 'Context';
import {
  useDataFetcher,
  buildRoute,
  useAddEditSuccessHandler,
  defaultDataTransform,
  addEditTitle,
  useToggler,
  fullClassName,
  toggledClass,
} from 'Utilities';
import { V3Role, InputType } from 'Models';
import 'Components/Form/SmartInputs/SmartCheckboxList.scss';

export const AddEditV3Role: React.FC = () => {
  const entityName = 'V3 Role';
  const { id } = useParams<{ id?: string }>();
  const isAdd = !id;
  const handleSuccess = useAddEditSuccessHandler(entityName, isAdd);
  const [data, fetchStatus] = useDataFetcher<V3Role>(
    buildRoute(ENDPOINTS.roles.base, isAdd ? undefined : id),
    new V3Role(),
    defaultDataTransform,
    V3Role,
    true,
    '3.0'
  );

  return (
    <AsyncLoadingSection loadingStatus={fetchStatus} useMask>
      <TaskPageHeaderLayout
        title={addEditTitle(entityName, isAdd)}
        formProviderProps={{
          formModel: data,
          endpoint: ENDPOINTS.roles.base,
          handleSuccess: handleSuccess,
        }}
        apiVersion="3.0"
      >
        <V3RolesAddEditFormContent />
      </TaskPageHeaderLayout>
    </AsyncLoadingSection>
  );
};

const V3RolesAddEditFormContent: React.FC = () => {
  return (
    <PageCard>
      <PageCardRow>
        <PageCardSection short>
          <Row>
            <SmartInputBlock
              name="name"
              label="Name"
              type={InputType.Text}
              saveOnDebounce
            />
            <SmartInputBlock
              name="code"
              label="Code"
              type={InputType.Text}
              saveOnDebounce
            />
            <SmartInputBlock
              name="description"
              label="Description"
              type={InputType.Text}
              saveOnDebounce
            />
            <SmartInputBlock
              name="isActive"
              label="Active"
              type={InputType.Checkbox}
              saveOnDebounce
            />
          </Row>
        </PageCardSection>
      </PageCardRow>
      <PageCardRow>
        <PageCardSection title="Permissions">
          <PermissionTree />
        </PageCardSection>
      </PageCardRow>
    </PageCard>
  );
};

// V3 permissions form a code hierarchy (e.g. REVIEWER / REVIEWER.PANEL / REVIEWER.PANEL.VIEW),
// each level its own independently-assignable V3Permission row (per the prefix-matching
// authorization model — see PermissionCodes.cs). SmartCheckboxList only supports one grouping
// level, and its group-header checkbox is a computed "select all in group" toggle rather than a
// real, independently-stored value, so it can't represent this. This builds a proper N-level tree
// instead, with every node (root, resource, leaf) bound to its own real `isSelected` field.
type PermissionListItem = {
  code: string;
  name: string;
  description?: string;
  groupName?: string;
  isSelected: boolean;
};

type PermissionTreeNode = PermissionListItem & { children: PermissionTreeNode[] };

const buildPermissionTree = (items: PermissionListItem[]): PermissionTreeNode[] => {
  const byCode = new Map<string, PermissionTreeNode>(
    items.map((item) => [item.code, { ...item, children: [] }])
  );
  const roots: PermissionTreeNode[] = [];

  // Some permissions have an extra code segment (e.g. REVIEWER.PROFILE.CONTACT.EDIT) with no
  // node of their own for the intermediate segment (REVIEWER.PROFILE.CONTACT). Walk up the
  // dot-hierarchy to the nearest ancestor that does exist, so these nest under their group
  // (REVIEWER.PROFILE) as a third level instead of falling out to the tree root.
  const findParent = (code: string): PermissionTreeNode | undefined => {
    let dotIndex = code.lastIndexOf('.');
    while (dotIndex !== -1) {
      const ancestor = byCode.get(code.substring(0, dotIndex));
      if (ancestor) return ancestor;
      dotIndex = code.lastIndexOf('.', dotIndex - 1);
    }
    return undefined;
  };

  byCode.forEach((node) => {
    const parent = findParent(node.code);
    (parent ? parent.children : roots).push(node);
  });

  const sortByName = (nodes: PermissionTreeNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach((node) => sortByName(node.children));
  };
  sortByName(roots);

  return roots;
};

// Leaf permissions are named "<Group> - <Verb>" (e.g. "Reviewer Panel - View"); nested under its
// own "Reviewer Panel" parent that's redundant, so this shows just the "View" part.
const formatLabel = (item: PermissionListItem) => {
  const prefix = `${item.groupName} - `;
  return item.name?.startsWith(prefix) ? item.name.substring(prefix.length) : item.name;
};

const PermissionTree: React.FC = () => {
  const [permissionsList] = useFormStateValue<PermissionListItem[]>('permissionsList');

  const codeToIndex = React.useMemo(() => {
    const map = new Map<string, number>();
    (permissionsList || []).forEach((item, index) => map.set(item.code, index));
    return map;
  }, [permissionsList]);

  const tree = React.useMemo(
    () => buildPermissionTree(permissionsList || []),
    [permissionsList]
  );

  return (
    <div className="checkbox-list">
      {tree.map((node) => (
        <PermissionTreeItem
          key={node.code}
          node={node}
          codeToIndex={codeToIndex}
          depth={0}
        />
      ))}
    </div>
  );
};

type PermissionTreeItemProps = {
  node: PermissionTreeNode;
  codeToIndex: Map<string, number>;
  depth: number;
};

const PermissionTreeItem: React.FC<PermissionTreeItemProps> = ({
  node,
  codeToIndex,
  depth,
}) => {
  const [isOpen, toggleIsOpen] = useToggler(false);
  const hasChildren = node.children.length > 0;

  return (
    <FormColumn className="py-1 checkbox-list-column" hideSeparator>
      <div
        style={{ marginLeft: depth * 24 }}
        className={fullClassName(
          'flex-normal flex-vertical-center checkbox-section-header',
          toggledClass('open', isOpen)
        )}
        title={node.description}
      >
        <SmartInputBlock
          name={`permissionsList[${codeToIndex.get(node.code)}].isSelected`}
          label={formatLabel(node)}
          type={InputType.Checkbox}
          noPaddingMargin
        />
        {hasChildren && (
          <div
            className={fullClassName(
              'collapse-arrow-wrapper ml-2',
              toggledClass('open', isOpen)
            )}
            onClick={toggleIsOpen}
          >
            <ExpandMoreIcon className="collapse-arrow" />
          </div>
        )}
      </div>
      {hasChildren && (
        <Collapse isOpened={isOpen}>
          {node.children.map((child) => (
            <PermissionTreeItem
              key={child.code}
              node={child}
              codeToIndex={codeToIndex}
              depth={depth + 1}
            />
          ))}
        </Collapse>
      )}
    </FormColumn>
  );
};
